import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DealerAuthProvider, useDealerAuth } from '../context/DealerAuthContext';
import { useTheme } from '@/context/ThemeContext';
import DealerLayout from '../components/DealerLayout';
import { useProductCatalog } from '@/context/ProductCatalogContext';
import { createDealerOrder } from '@/lib/api/orders';
import type { Product } from '@/mocks/products';

interface CartItem {
  productId: string;
  productName: string;
  category: Product['category'];
  unitPrice: number;
  qty: number;
}

const catColor: Record<string, string> = {
  lithium: 'bg-[#F97316]/20 text-[#F97316]',
  agm: 'bg-green-500/20 text-green-400',
  gel: 'bg-yellow-500/20 text-yellow-400',
};

type Step = 'products' | 'review';

function NewOrderContent() {
  const { theme } = useTheme();
  const { products } = useProductCatalog();
  const { dealer } = useDealerAuth();
  const dark = theme === 'dark';
  const [step, setStep] = useState<Step>('products');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [notes, setNotes] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedOrderId, setSubmittedOrderId] = useState('');
  const [catFilter, setCatFilter] = useState<'all' | 'lithium' | 'agm' | 'gel'>('all');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const card = dark ? 'bg-[#1C2B3A] border-white/5' : 'bg-white border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/40' : 'text-gray-400';
  const borderB = dark ? 'border-white/10' : 'border-gray-200';
  const divider = dark ? 'divide-white/5' : 'divide-gray-100';
  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[#F97316]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#F97316]';
  const inactiveFilter = dark ? 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:text-gray-900 hover:bg-gray-200';
  const stepInactive = dark ? 'text-white/40' : 'text-gray-400';
  const stepCircleInactive = dark ? 'bg-white/10 text-white/40' : 'bg-gray-200 text-gray-400';
  const dividerLine = dark ? 'bg-white/10' : 'bg-gray-200';
  const productCard = (selected: boolean) => dark
    ? `bg-[#1C2B3A] border rounded-lg p-4 transition-all ${selected ? 'border-[#F97316]/40' : 'border-white/5'}`
    : `bg-white border rounded-lg p-4 transition-all ${selected ? 'border-[#F97316]' : 'border-gray-200'}`;
  const productImgBg = dark ? 'bg-[#0D1B2A]' : 'bg-gray-100';
  const qtyBtn = dark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200';
  const cartEmpty = dark ? 'text-white/30' : 'text-gray-300';
  const cartItem = dark ? 'text-white/40' : 'text-gray-400';
  const backBtn = dark ? 'border-white/20 text-white/50 hover:text-white' : 'border-gray-200 text-gray-500 hover:text-gray-900';
  const checkIcon = 'text-green-400';
  const checkText = dark ? 'text-white/50' : 'text-gray-500';

  useEffect(() => {
    if (dealer?.address) {
      setShippingAddress(dealer.address);
    }
  }, [dealer?.address]);

  const filteredProducts = catFilter === 'all' ? products : products.filter((p) => p.category === catFilter);
  const getQty = (productId: string) => cart.find((c) => c.productId === productId)?.qty ?? 0;

  const updateQty = (product: typeof products[0], qty: number) => {
    if (qty <= 0) {
      setCart((prev) => prev.filter((c) => c.productId !== product.id));
    } else {
      setCart((prev) => {
        const existing = prev.find((c) => c.productId === product.id);
        if (existing) return prev.map((c) => c.productId === product.id ? { ...c, qty } : c);
        return [...prev, { productId: product.id, productName: product.name, category: product.category, unitPrice: product.price, qty }];
      });
    }
  };

  const cartTotal = cart.reduce((s, c) => s + c.unitPrice * c.qty, 0);
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-500/20 mb-6">
          <i className="ri-check-double-line text-green-400 text-4xl"></i>
        </div>
        <h2 className={`text-2xl font-black mb-2 ${text}`}>Order Submitted!</h2>
        <p className={`text-sm mb-1 ${muted}`}>Your order <span className="text-[#F97316] font-bold">{submittedOrderId || 'is being processed'}</span> has been received.</p>
        <p className={`text-xs mb-8 ${muted}`}>Our team will confirm your order within 1 business day.</p>
        <div className="flex gap-3">
          <Link to="/dealer/orders" className="bg-[#F97316] text-white font-bold px-6 py-2.5 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap text-sm">
            View My Orders
          </Link>
          <button
            onClick={() => { setSubmitted(false); setSubmittedOrderId(''); setCart([]); setNotes(''); setStep('products'); }}
            className={`border font-semibold px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm ${backBtn}`}
          >
            Place Another Order
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-2 text-sm font-semibold ${step === 'products' ? 'text-[#F97316]' : stepInactive}`}>
          <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-black ${step === 'products' ? 'bg-[#F97316] text-white' : stepCircleInactive}`}>1</div>
          Select Products
        </div>
        <div className={`flex-1 h-px max-w-16 ${dividerLine}`}></div>
        <div className={`flex items-center gap-2 text-sm font-semibold ${step === 'review' ? 'text-[#F97316]' : stepInactive}`}>
          <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-black ${step === 'review' ? 'bg-[#F97316] text-white' : stepCircleInactive}`}>2</div>
          Review &amp; Submit
        </div>
      </div>

      {step === 'products' && (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 space-y-4">
            <div className="flex flex-wrap gap-2">
              {(['all', 'lithium', 'agm', 'gel'] as const).map((cat) => (
                <button key={cat} onClick={() => setCatFilter(cat)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${catFilter === cat ? 'bg-[#F97316] text-white' : inactiveFilter}`}>
                  {cat === 'all' ? 'All Products' : cat === 'lithium' ? 'Lithium LiFePO4' : cat === 'agm' ? 'AGM Series' : 'Gel Series'}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredProducts.map((product) => {
                const qty = getQty(product.id);
                return (
                  <div key={product.id} className={productCard(qty > 0)}>
                    <div className="flex gap-3 mb-3">
                      <div className={`w-14 h-14 rounded-lg overflow-hidden shrink-0 ${productImgBg}`}>
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full capitalize mb-1 ${catColor[product.category]}`}>
                          {product.category}
                        </span>
                        <p className={`text-sm font-bold leading-tight truncate ${text}`}>{product.name}</p>
                        <p className="text-[#F97316] font-black text-sm">${product.price.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className={`text-xs flex-1 ${muted}`}>{product.capacity}Ah &bull; {product.voltage}V</p>
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateQty(product, qty - 1)} disabled={qty === 0}
                          className={`w-7 h-7 flex items-center justify-center rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer ${qtyBtn}`}>
                          <i className="ri-subtract-line text-xs"></i>
                        </button>
                        <span className={`w-8 text-center text-sm font-black ${qty > 0 ? 'text-[#F97316]' : muted}`}>{qty}</span>
                        <button onClick={() => updateQty(product, qty + 1)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#F97316] text-white hover:bg-[#ea6a0a] transition-colors cursor-pointer">
                          <i className="ri-add-line text-xs"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Cart summary */}
          <div className="lg:w-72 shrink-0">
            <div className={`border rounded-lg overflow-hidden sticky top-6 ${card}`}>
              <div className={`px-5 py-4 border-b ${borderB}`}>
                <h3 className={`font-bold text-sm ${text}`}>Order Summary</h3>
                <p className={`text-xs mt-0.5 ${muted}`}>{cartCount} item{cartCount !== 1 ? 's' : ''} selected</p>
              </div>
              {cart.length === 0 ? (
                <div className={`px-5 py-8 text-center ${cartEmpty}`}>
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-shopping-cart-line text-2xl"></i>
                  </div>
                  <p className="text-xs">No products selected yet</p>
                </div>
              ) : (
                <div className={`divide-y ${divider}`}>
                  {cart.map((item) => (
                    <div key={item.productId} className="px-5 py-3 flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-semibold truncate ${text}`}>{item.productName.replace('Stingray ', '')}</p>
                        <p className={`text-xs ${cartItem}`}>${item.unitPrice.toLocaleString()} × {item.qty}</p>
                      </div>
                      <p className={`text-sm font-bold shrink-0 ${text}`}>${(item.unitPrice * item.qty).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className={`px-5 py-4 border-t ${borderB}`}>
                <div className="flex items-center justify-between mb-4">
                  <p className={`text-sm ${muted}`}>Total</p>
                  <p className="text-[#F97316] text-xl font-black">${cartTotal.toLocaleString()}</p>
                </div>
                <button onClick={() => setStep('review')} disabled={cart.length === 0}
                  className="w-full bg-[#F97316] text-white font-bold py-3 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                  Continue to Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 'review' && (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 space-y-5">
            <div className={`border rounded-lg overflow-hidden ${card}`}>
              <div className={`px-5 py-4 border-b flex items-center justify-between ${borderB}`}>
                <h3 className={`font-bold text-sm ${text}`}>Order Items</h3>
                <button onClick={() => setStep('products')} className="text-[#F97316] text-xs font-semibold hover:underline cursor-pointer whitespace-nowrap">Edit</button>
              </div>
              <div className={`divide-y ${divider}`}>
                {cart.map((item) => (
                  <div key={item.productId} className="px-5 py-4 flex items-center justify-between gap-4">
                    <div>
                      <p className={`text-sm font-semibold ${text}`}>{item.productName}</p>
                      <p className={`text-xs mt-0.5 capitalize ${muted}`}>{item.category} &bull; ${item.unitPrice.toLocaleString()} each</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-xs ${muted}`}>Qty: {item.qty}</p>
                      <p className={`font-bold text-sm ${text}`}>${(item.unitPrice * item.qty).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className={`px-5 py-3 border-t flex items-center justify-between ${borderB}`}>
                <p className={`text-sm font-semibold ${muted}`}>Order Total</p>
                <p className="text-[#F97316] text-xl font-black">${cartTotal.toLocaleString()}</p>
              </div>
            </div>

            <div className={`border rounded-lg p-5 ${card}`}>
              <h3 className={`font-bold text-sm mb-3 ${text}`}>Shipping Address</h3>
              <textarea rows={3} value={shippingAddress} onChange={(e) => setShippingAddress(e.target.value)}
                className={`w-full border text-sm px-4 py-3 rounded-lg focus:outline-none transition-colors resize-none ${inputCls}`} />
            </div>

            <div className={`border rounded-lg p-5 ${card}`}>
              <h3 className={`font-bold text-sm mb-3 ${text}`}>Order Notes <span className={`font-normal ${muted}`}>(optional)</span></h3>
              <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special instructions, delivery preferences, etc." maxLength={500}
                className={`w-full border text-sm px-4 py-3 rounded-lg focus:outline-none transition-colors resize-none ${inputCls}`} />
              <p className={`text-xs mt-1 text-right ${muted}`}>{notes.length}/500</p>
            </div>
          </div>

          <div className="lg:w-72 shrink-0">
            <div className={`border rounded-lg p-5 sticky top-6 space-y-4 ${card}`}>
              <h3 className={`font-bold text-sm ${text}`}>Ready to Submit?</h3>
              <div className="space-y-2 text-xs">
                {['No payment required at this stage', 'Confirmation within 1 business day', 'Shipping details sent via email'].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-check-line ${checkIcon}`}></i>
                    </div>
                    <span className={checkText}>{item}</span>
                  </div>
                ))}
              </div>
              <div className={`border-t pt-4 ${borderB}`}>
                <div className="flex items-center justify-between mb-4">
                  <p className={`text-sm ${muted}`}>Total</p>
                  <p className="text-[#F97316] text-xl font-black">${cartTotal.toLocaleString()}</p>
                </div>
                {submitError && (
                  <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-3">
                    {submitError}
                  </p>
                )}
                <button
                  onClick={async () => {
                    setSubmitError('');
                    setSubmitting(true);
                    try {
                      const order = await createDealerOrder({
                        shippingAddress,
                        notes,
                        items: cart.map((item) => ({
                          productId: item.productId,
                          productName: item.productName,
                          category: item.category,
                          qty: item.qty,
                          unitPrice: item.unitPrice,
                        })),
                      });
                      setSubmittedOrderId(order.id);
                      setSubmitted(true);
                    } catch (err) {
                      setSubmitError(err instanceof Error ? err.message : 'Unable to submit your order.');
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  disabled={submitting || !shippingAddress.trim()}
                  className="w-full bg-[#F97316] text-white font-bold py-3.5 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap text-sm uppercase tracking-wide disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting ? 'Submitting...' : 'Submit Order'}
                </button>
                <button onClick={() => setStep('products')}
                  className={`w-full mt-2 border font-semibold py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm ${backBtn}`}>
                  Back to Products
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DealerNewOrderPage() {
  return (
    <DealerAuthProvider>
      <DealerLayout title="New Order">
        <NewOrderContent />
      </DealerLayout>
    </DealerAuthProvider>
  );
}
